import React from "react";
import MutedText from "../../ui/MutedText";
import MenuSelect from "../../ui/MenuSelect";
import {
  BodySelect,
  EndEffectorSelect,
  INPUT_CLASS,
  NumericDragRow,
  RequiredFieldLabel,
  Row,
  XYZRow,
} from "./NodeInspectorFields";

export default function NodeParameterFields({
  bodyExists,
  eeOpts,
  endEffectorExists,
  getFieldError,
  node,
  onChange,
  opts,
  trainedModel,
  trainedModelRuntime,
  validateNumeric,
}) {
  return (
    <>
      {node.kind === "servoPickPlace" ? (
        <>
          <Row
            label={
              <RequiredFieldLabel required={!bodyExists(node.pickBodyUuid)}>
                Pick body
              </RequiredFieldLabel>
            }
          >
            <BodySelect
              opts={opts}
              value={node.pickBodyUuid}
              onPick={(v) => onChange({ ...node, pickBodyUuid: v })}
            />
          </Row>
          <Row label="Drop body">
            <BodySelect
              opts={opts}
              value={node.dropBodyUuid}
              onPick={(v) => onChange({ ...node, dropBodyUuid: v })}
              placeholder="— optional —"
            />
          </Row>
          <Row
            label={
              <RequiredFieldLabel required={!endEffectorExists(node.endEffectorRef)}>
                End-effector
              </RequiredFieldLabel>
            }
          >
            <EndEffectorSelect
              opts={eeOpts}
              value={node.endEffectorRef}
              onPick={(v) => onChange({ ...node, endEffectorRef: v })}
            />
          </Row>
        </>
      ) : null}

      {(node.kind === "moveEeToXyz" || node.kind === "moveEeByOffset") && (
        <>
          <Row
            label={
              <RequiredFieldLabel required={!endEffectorExists(node.endEffectorRef)}>
                End-effector ref
              </RequiredFieldLabel>
            }
          >
            <EndEffectorSelect
              opts={eeOpts}
              value={node.endEffectorRef}
              onPick={(v) => onChange({ ...node, endEffectorRef: v })}
            />
          </Row>
          {node.kind === "moveEeToXyz" ? (
            <>
              <Row label="Target body (optional)">
                <BodySelect
                  opts={opts}
                  value={node.targetBodyUuid || ""}
                  placeholder="— absolute world target —"
                  onPick={(v) => onChange({ ...node, targetBodyUuid: v })}
                />
              </Row>
              <Row label={node.targetBodyUuid ? "Target offset XYZ (m)" : "Target XYZ (m)"}>
                <XYZRow
                  keys={["x", "y", "z"]}
                  node={node}
                  onChange={onChange}
                  fieldPrefix="moveEeToXyz"
                  validateNumeric={validateNumeric}
                  getFieldError={getFieldError}
                />
              </Row>
            </>
          ) : (
            <Row label="Offset ΔXYZ (m)">
              <XYZRow
                keys={["dx", "dy", "dz"]}
                node={node}
                onChange={onChange}
                fieldPrefix="moveEeByOffset"
                validateNumeric={validateNumeric}
                getFieldError={getFieldError}
              />
            </Row>
          )}
          <Row label="Orientation constraint">
            <MenuSelect
              size="compact"
              className={INPUT_CLASS}
              value={
                node.orientationMode === "alignWithGrasp"
                  ? "alignWithGrasp"
                  : node.orientationMode === "keepDown"
                    ? "keepDown"
                    : "free"
              }
              onChange={(raw) => {
                const mode =
                  raw === "alignWithGrasp"
                    ? "alignWithGrasp"
                    : raw === "keepDown"
                      ? "keepDown"
                      : "free";
                onChange({ ...node, orientationMode: mode });
              }}
              options={[
                { value: "free", label: "Free orientation" },
                { value: "keepDown", label: "Keep gripper pointing down" },
                { value: "alignWithGrasp", label: "Align with best grasp (if available)" },
              ]}
              contentClassName="max-h-[min(240px,50vh)]"
              triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
              aria-label="Orientation constraint"
            />
          </Row>
          {node.orientationMode === "alignWithGrasp" &&
          node.kind === "moveEeToXyz" &&
          !node.targetBodyUuid ? (
            <MutedText className="text-xs leading-snug">
              Align with grasp requires a Target body with grasp data; otherwise it falls back to
              Keep Down.
            </MutedText>
          ) : null}
          {node.orientationMode === "keepDown" || node.orientationMode === "alignWithGrasp" ? (
            <NumericDragRow
              label="Orientation strength"
              fieldKey={`${node.kind}.orientationStrength`}
              value={node.orientationStrength ?? 0.8}
              onCommit={(next) => onChange({ ...node, orientationStrength: next })}
              validateNumeric={validateNumeric}
              getFieldError={getFieldError}
              min={0}
              max={2}
              step="0.05"
              dragSensitivity={0.05}
              dragSnapStep={0.05}
            />
          ) : null}
          <NumericDragRow
            label="Settle time (seconds)"
            fieldKey={`${node.kind}.settleSeconds`}
            value={node.settleSeconds}
            onCommit={(next) => onChange({ ...node, settleSeconds: next })}
            validateNumeric={validateNumeric}
            getFieldError={getFieldError}
            min={0}
            step="0.05"
            dragSensitivity={0.05}
            dragSnapStep={0.05}
          />
        </>
      )}

      {node.kind === "goToKeyframe" && (
        <Row
          label={
            <RequiredFieldLabel required={!node.keyframeName?.trim()}>
              Keyframe name
            </RequiredFieldLabel>
          }
        >
          <input
            className={INPUT_CLASS}
            value={node.keyframeName}
            onChange={(e) => onChange({ ...node, keyframeName: e.target.value })}
          />
        </Row>
      )}

      {node.kind === "runTrainedModel" && (
        <>
          <Row label={<RequiredFieldLabel required={!trainedModel}>Model</RequiredFieldLabel>}>
            <input
              className={`${INPUT_CLASS} cursor-default opacity-90`}
              value={trainedModel?.name || node.modelId}
              readOnly
            />
          </Row>
          <Row label="Robot">
            <input
              className={`${INPUT_CLASS} cursor-default opacity-90`}
              value={trainedModel?.robotLabel || "unknown"}
              readOnly
            />
          </Row>
          <NumericDragRow
            label="Duration (seconds)"
            fieldKey="runTrainedModel.durationSeconds"
            value={node.durationSeconds}
            onCommit={(next) => onChange({ ...node, durationSeconds: next })}
            validateNumeric={validateNumeric}
            getFieldError={getFieldError}
            min={0}
            step="0.1"
            dragSensitivity={0.1}
            dragSnapStep={0.1}
          />
          <NumericDragRow
            label="Command Vx (m/s)"
            fieldKey="runTrainedModel.commandVx"
            value={node.commandVx}
            onCommit={(next) => onChange({ ...node, commandVx: next })}
            validateNumeric={validateNumeric}
            getFieldError={getFieldError}
            step="0.05"
            dragSensitivity={0.05}
            dragSnapStep={0.05}
          />
          <NumericDragRow
            label="Command Wz (rad/s)"
            fieldKey="runTrainedModel.commandWz"
            value={node.commandWz}
            onCommit={(next) => onChange({ ...node, commandWz: next })}
            validateNumeric={validateNumeric}
            getFieldError={getFieldError}
            step="0.05"
            dragSensitivity={0.05}
            dragSnapStep={0.05}
          />
          <Row label="Runtime status">
            <input
              className={`${INPUT_CLASS} cursor-default opacity-90`}
              value={trainedModelRuntime.phase}
              readOnly
            />
          </Row>
          <Row label="Last inference (ms)">
            <input
              className={`${INPUT_CLASS} cursor-default opacity-90`}
              value={
                Number.isFinite(trainedModelRuntime.lastInferenceMs)
                  ? Number(trainedModelRuntime.lastInferenceMs).toFixed(1)
                  : "—"
              }
              readOnly
            />
          </Row>
          {trainedModelRuntime.lastError ? (
            <div className="rounded border border-rose-700/70 bg-rose-950/30 px-2 py-1.5 text-xs leading-snug text-rose-200">
              <div className="font-semibold">Inference error</div>
              <div className="mt-0.5">{trainedModelRuntime.lastError}</div>
            </div>
          ) : null}
          <MutedText className="text-xs leading-snug">
            This node resolves from the remote `critter-models` catalog and drives live browser-side
            ONNX inference while the behavior is running, then finishes after the configured
            duration.
          </MutedText>
        </>
      )}

      {node.kind === "wait" && (
        <NumericDragRow
          label="Seconds"
          fieldKey="wait.seconds"
          value={node.seconds}
          onCommit={(next) => onChange({ ...node, seconds: next })}
          validateNumeric={validateNumeric}
          getFieldError={getFieldError}
          min={0}
          step="0.05"
          dragSensitivity={0.1}
        />
      )}

      {node.kind === "randomizeObjectPose" && (
        <>
          <Row
            label={
              <RequiredFieldLabel required={!bodyExists(node.bodyUuid)}>Body</RequiredFieldLabel>
            }
          >
            <BodySelect
              opts={opts}
              value={node.bodyUuid}
              onPick={(v) => onChange({ ...node, bodyUuid: v })}
            />
          </Row>
          <NumericDragRow
            label="Radius XY (m)"
            fieldKey="randomizeObjectPose.radiusXY"
            value={node.radiusXY}
            onCommit={(next) => onChange({ ...node, radiusXY: next })}
            validateNumeric={validateNumeric}
            getFieldError={getFieldError}
            min={0}
            step="0.01"
          />
          <NumericDragRow
            label="Yaw range (deg)"
            fieldKey="randomizeObjectPose.yawRangeDeg"
            value={node.yawRangeDeg}
            onCommit={(next) => onChange({ ...node, yawRangeDeg: next })}
            validateNumeric={validateNumeric}
            getFieldError={getFieldError}
            min={0}
            step="1"
            dragSensitivity={0.5}
          />
        </>
      )}

      {node.kind === "setObjectPose" && (
        <>
          <Row
            label={
              <RequiredFieldLabel required={!bodyExists(node.bodyUuid)}>Body</RequiredFieldLabel>
            }
          >
            <BodySelect
              opts={opts}
              value={node.bodyUuid}
              onPick={(v) => onChange({ ...node, bodyUuid: v })}
            />
          </Row>
          <Row label="Position (m)">
            <XYZRow
              keys={["x", "y", "z"]}
              node={node}
              onChange={onChange}
              fieldPrefix="setObjectPose"
              validateNumeric={validateNumeric}
              getFieldError={getFieldError}
            />
          </Row>
        </>
      )}

      {(node.kind === "retry" || node.kind === "repeat") && (
        <NumericDragRow
          label={node.kind === "retry" ? "Max attempts" : "Repeat count"}
          fieldKey={node.kind === "retry" ? "retry.maxAttempts" : "repeat.count"}
          value={node.kind === "retry" ? node.maxAttempts : node.count}
          onCommit={(next) =>
            node.kind === "retry"
              ? onChange({ ...node, maxAttempts: next })
              : onChange({ ...node, count: next })
          }
          validateNumeric={validateNumeric}
          getFieldError={getFieldError}
          min={1}
          integer
          step="1"
          dragSensitivity={1}
        />
      )}

      {node.kind === "conditionObjectGrasped" && (
        <>
          <Row
            label={
              <RequiredFieldLabel required={!bodyExists(node.bodyUuid)}>Body</RequiredFieldLabel>
            }
          >
            <BodySelect
              opts={opts}
              value={node.bodyUuid}
              onPick={(v) => onChange({ ...node, bodyUuid: v })}
            />
          </Row>
          <NumericDragRow
            label="Min height Y"
            fieldKey="conditionObjectGrasped.minHeightY"
            value={node.minHeightY}
            onCommit={(next) => onChange({ ...node, minHeightY: next })}
            validateNumeric={validateNumeric}
            getFieldError={getFieldError}
            step="0.01"
            dragSensitivity={0.002}
          />
        </>
      )}

      {(node.kind === "conditionEeNear" || node.kind === "conditionObjectNear") && (
        <>
          {node.kind === "conditionObjectNear" ? (
            <Row
              label={
                <RequiredFieldLabel required={!bodyExists(node.bodyUuid)}>Body</RequiredFieldLabel>
              }
            >
              <BodySelect
                opts={opts}
                value={node.bodyUuid}
                onPick={(v) => onChange({ ...node, bodyUuid: v })}
              />
            </Row>
          ) : (
            <Row
              label={
                <RequiredFieldLabel required={!endEffectorExists(node.endEffectorRef)}>
                  EE ref
                </RequiredFieldLabel>
              }
            >
              <EndEffectorSelect
                opts={eeOpts}
                value={node.endEffectorRef}
                onPick={(v) => onChange({ ...node, endEffectorRef: v })}
              />
            </Row>
          )}
          <Row label="Target body (optional)">
            <BodySelect
              opts={opts}
              value={node.targetBodyUuid || ""}
              onPick={(v) => onChange({ ...node, targetBodyUuid: v })}
              placeholder="— none (use world XYZ) —"
            />
          </Row>
          <Row label={node.targetBodyUuid ? "Target offset XYZ (m)" : "Target XYZ (m)"}>
            <XYZRow
              keys={["x", "y", "z"]}
              node={node}
              onChange={onChange}
              fieldPrefix={node.kind}
              validateNumeric={validateNumeric}
              getFieldError={getFieldError}
            />
          </Row>
          <NumericDragRow
            label="Tolerance (m)"
            fieldKey={`${node.kind}.toleranceM`}
            value={node.toleranceM}
            onCommit={(next) => onChange({ ...node, toleranceM: next })}
            validateNumeric={validateNumeric}
            getFieldError={getFieldError}
            min={0}
            step="0.01"
          />
        </>
      )}

      {node.kind === "conditionTimeElapsed" && (
        <NumericDragRow
          label="Seconds"
          fieldKey="conditionTimeElapsed.seconds"
          value={node.seconds}
          onCommit={(next) => onChange({ ...node, seconds: next })}
          validateNumeric={validateNumeric}
          getFieldError={getFieldError}
          min={0}
          step="0.1"
          dragSensitivity={0.1}
          dragSnapStep={0.1}
        />
      )}

      {(node.kind === "openGripper" || node.kind === "closeGripper") && (
        <NumericDragRow
          label="Settle time (seconds)"
          fieldKey={`${node.kind}.settleSeconds`}
          value={node.settleSeconds}
          onCommit={(next) => onChange({ ...node, settleSeconds: next })}
          validateNumeric={validateNumeric}
          getFieldError={getFieldError}
          min={0}
          step="0.05"
          dragSensitivity={0.05}
          dragSnapStep={0.05}
        />
      )}

      {(node.kind === "resetToBaseline" ||
        node.kind === "markSuccess" ||
        node.kind === "markFailure" ||
        node.kind === "ifThenElse") && (
        <MutedText className="text-xs italic">No parameters for this node.</MutedText>
      )}
    </>
  );
}
